import * as React from "react"
import { Popover as PopoverPrimitive } from "@base-ui/react/popover"

import { cn } from "@/lib/utils"
import { resolveAsChild } from "./as-child"

/**
 * Radix had a separate Anchor part that positioned the popup without being a
 * trigger. Base UI positions from the Positioner's `anchor`, so the Anchor
 * stores its element here and the content anchors to it. Making the anchor a
 * Trigger instead would give a text input button semantics and toggle the
 * popup on click.
 */
const PopoverAnchorContext = React.createContext<{
  anchor: Element | null
  setAnchor: (element: Element | null) => void
} | null>(null)

function Popover({ children, ...props }: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  const [anchor, setAnchor] = React.useState<Element | null>(null)
  const value = React.useMemo(() => ({ anchor, setAnchor }), [anchor])

  return (
    <PopoverAnchorContext.Provider value={value}>
      <PopoverPrimitive.Root {...props}>{children}</PopoverPrimitive.Root>
    </PopoverAnchorContext.Provider>
  )
}

function PopoverTrigger({
  asChild,
  render,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger> & {
  /** Radix's `asChild`, bridged onto Base UI's `render`. */
  asChild?: boolean
}) {
  const resolved = resolveAsChild(asChild, render, props)

  return (
    <PopoverPrimitive.Trigger
      data-slot="popover-trigger"
      render={resolved.render}
      {...resolved.props}
    />
  )
}

function PopoverAnchor({
  asChild,
  render,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  asChild?: boolean
  render?: React.ReactElement
}) {
  const context = React.useContext(PopoverAnchorContext)
  const ref = React.useCallback(
    (element: HTMLElement | null) => context?.setAnchor(element),
    [context],
  )

  const child = render ?? (asChild ? children : undefined)

  // The anchor is a positioning reference only: the child keeps its own
  // semantics (an <input> stays an input) and gains no click handler.
  if (React.isValidElement(child)) {
    return React.cloneElement(child as React.ReactElement<{ ref?: React.Ref<HTMLElement> }>, { ref })
  }

  return (
    <div ref={ref} data-slot="popover-anchor" {...props}>
      {children}
    </div>
  )
}

const PopoverContent = React.forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Popup>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Popup> & {
    align?: "start" | "center" | "end"
    side?: React.ComponentProps<typeof PopoverPrimitive.Positioner>["side"]
    sideOffset?: number
    /**
     * Radix's focus hook. Call `preventDefault()` to keep focus where it is
     * (the autocompletes rely on this to keep typing in their input); Base UI
     * expresses that as `initialFocus={false}`.
     */
    onOpenAutoFocus?: (event: { preventDefault: () => void }) => void
  }
>(({ className, align = "center", side, sideOffset = 4, style, onOpenAutoFocus, ...props }, ref) => {
  const context = React.useContext(PopoverAnchorContext)

  const initialFocus = React.useMemo(() => {
    if (!onOpenAutoFocus) {
      return undefined
    }
    let prevented = false
    onOpenAutoFocus({ preventDefault: () => { prevented = true } })
    return prevented ? false : undefined
  }, [onOpenAutoFocus])

  return (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Positioner
      anchor={context?.anchor ?? undefined}
      align={align}
      side={side}
      sideOffset={sideOffset}
      // Existing call sites size themselves with Radix's variable name; alias
      // it to Base UI's so those classNames keep working.
      style={{ ["--radix-popover-trigger-width" as string]: "var(--anchor-width)" }}
    >
      <PopoverPrimitive.Popup
        ref={ref}
        data-slot="popover-content"
        style={style}
        className={cn(
          "z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground  outline-none data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className
        )}
        initialFocus={initialFocus}
        {...props}
      />
    </PopoverPrimitive.Positioner>
  </PopoverPrimitive.Portal>
  )
})
PopoverContent.displayName = "PopoverContent"

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor }
